import React, { useEffect, useState } from 'react';
import { Application } from '../services/applicationApi';
import { candidateServiceApi, CandidateProfile } from '../../candidate/services/candidateServiceApi';
import { X, Mail, FileText, Calendar, Briefcase, ExternalLink, MessageSquare, AlertCircle, GraduationCap, Code, Loader2 } from 'lucide-react';

// Types for parsed resume data
interface WorkExperience {
  company?: string;
  title?: string;
  startDate?: string;
  endDate?: string;
  isCurrent?: boolean;
  description?: string;
  location?: string;
}

interface Education {
  institution?: string;
  degree?: string;
  fieldOfStudy?: string;
  startDate?: string;
  startYear?: string;
  endDate?: string;
  endYear?: string;
  gpa?: string;
}

interface CandidateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
  onStageChange: (newStage: Application['currentStage']) => void;
  onMessage: () => void;
  updatingStage: boolean;
  stageError: string | null;
  onClearError: () => void;
  validTransitions: Record<string, string[]>;
  messagingAllowedStages: string[];
}

// Simplified flow: Applied → Interview → Offer → Rejected
const STAGE_OPTIONS: { value: Application['currentStage']; label: string }[] = [
  { value: 'applied', label: 'Applied' },
  { value: 'interview', label: 'Interview' },
  { value: 'offered', label: 'Offer' },
  { value: 'rejected', label: 'Rejected' },
];

/**
 * Candidate Profile Modal
 * Shows candidate details, resume, and allows stage management and messaging
 */
const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  isOpen,
  onClose,
  application,
  onStageChange,
  onMessage,
  updatingStage,
  stageError,
  onClearError,
  validTransitions,
  messagingAllowedStages,
}) => {
  const [candidateProfile, setCandidateProfile] = useState<CandidateProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [resumePresignedUrl, setResumePresignedUrl] = useState<string | null>(null);
  const [loadingResume, setLoadingResume] = useState(false);

  // Fetch full candidate profile when modal opens
  useEffect(() => {
    const fetchCandidateProfile = async () => {
      if (!isOpen || !application?.candidateId) {
        setCandidateProfile(null);
        setResumePresignedUrl(null);
        return;
      }

      setLoadingProfile(true);
      setProfileError(null);

      try {
        const response = await candidateServiceApi.getCandidateById(application.candidateId);
        if (response.success && response.candidate) {
          setCandidateProfile(response.candidate);
        } else {
          setProfileError(response.message || 'Failed to load candidate profile');
        }
      } catch (error: any) {
        console.error('Error fetching candidate profile:', error);
        setProfileError(error.message || 'Failed to load candidate profile');
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchCandidateProfile();
  }, [isOpen, application?.candidateId]);

  // Fetch presigned URL for resume when candidate profile is loaded
  useEffect(() => {
    const fetchResumePresignedUrl = async () => {
      if (!isOpen || !application?.candidateId || !candidateProfile?.resumeS3Key) {
        setResumePresignedUrl(null);
        return;
      }

      setLoadingResume(true);
      try {
        const response = await candidateServiceApi.getResumePresignedUrl(application.candidateId);
        if (response.success && response.presignedUrl) {
          setResumePresignedUrl(response.presignedUrl);
        } else {
          console.warn('Failed to get resume presigned URL:', response.error);
          setResumePresignedUrl(null);
        }
      } catch (error) {
        console.error('Error fetching resume presigned URL:', error);
        setResumePresignedUrl(null);
      } finally {
        setLoadingResume(false);
      }
    };

    fetchResumePresignedUrl();
  }, [isOpen, application?.candidateId, candidateProfile?.resumeS3Key]);

  if (!isOpen || !application) return null;

  // Extract data from candidate profile's parsedResume
  const parsedResume = candidateProfile?.parsedResume;
  const skills: string[] = parsedResume?.skills || [];
  const experience: WorkExperience[] = parsedResume?.experience || [];
  const education: Education[] = parsedResume?.education || [];
  const summary: string = parsedResume?.summary || '';
  const totalYearsExp: number = parsedResume?.totalYearsExp || 0;

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'interview':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canTransitionTo = (newStage: string): boolean => {
    const allowed = validTransitions[application.currentStage] || [];
    return allowed.includes(newStage);
  };

  const canMessage = messagingAllowedStages.includes(application.currentStage);

  const getAvailableStages = (): string[] => {
    return validTransitions[application.currentStage] || [];
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Side - Resume/Details */}
        <div className="w-2/5 bg-[#F9F9F9] border-r border-[#EDEDED] overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Loading State */}
            {loadingProfile && (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-[#015FA6]" />
                <span className="ml-2 text-sm text-[#757575]">Loading profile...</span>
              </div>
            )}

            {/* Profile Error */}
            {profileError && !loadingProfile && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{profileError}</p>
              </div>
            )}

            {/* Resume Section */}
            <div>
              <h3 className="text-lg font-semibold text-[#0B111E] mb-4 flex items-center gap-2">
                <FileText size={20} />
                Resume
              </h3>

              {candidateProfile?.resumeS3Key ? (
                <div className="space-y-3">
                  <div className="bg-white rounded-lg border border-[#EDEDED] p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-[#0B111E]">Candidate Resume</span>
                      {resumePresignedUrl && (
                        <a
                          href={resumePresignedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#015FA6] text-sm hover:underline"
                        >
                          <ExternalLink size={14} />
                          Open in new tab
                        </a>
                      )}
                    </div>

                    {loadingResume ? (
                      <div className="flex items-center justify-center h-[300px]">
                        <div className="text-center">
                          <Loader2 size={24} className="animate-spin text-[#015FA6] mx-auto" />
                          <p className="text-sm text-[#757575] mt-2">Loading resume...</p>
                        </div>
                      </div>
                    ) : resumePresignedUrl ? (
                      <iframe
                        src={resumePresignedUrl}
                        className="w-full h-[400px] rounded border border-[#EDEDED]"
                        title="Resume Preview"
                      />
                    ) : (
                      <div className="bg-[#F1F3F4] rounded-lg p-6 text-center h-[300px] flex flex-col items-center justify-center">
                        <FileText size={40} className="mx-auto mb-2 text-[#757575]" />
                        <p className="text-sm text-[#757575]">Unable to load resume preview</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-[#EDEDED] p-6 text-center">
                  <FileText size={40} className="mx-auto mb-2 text-[#A5A5A5]" />
                  <p className="text-sm text-[#757575]">No resume uploaded</p>
                </div>
              )}
            </div>

            {/* Summary Section - only show if summary has meaningful content */}
            {summary && !summary.toLowerCase().includes('professional with 0 years') && summary.trim().length > 20 && (
              <div>
                <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Summary</h3>
                <div className="bg-white rounded-lg border border-[#EDEDED] p-4">
                  <p className="text-sm text-[#515151]">{summary}</p>
                  {totalYearsExp > 0 && (
                    <p className="text-xs text-[#757575] mt-2">
                      {totalYearsExp} years of experience
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Skills Section */}
            {skills.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#0B111E] mb-3 flex items-center gap-2">
                  <Code size={16} />
                  Skills
                </h3>
                <div className="bg-white rounded-lg border border-[#EDEDED] p-4">
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-[#E3F2FD] text-[#015FA6] text-xs font-medium rounded"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Experience Section */}
            {experience.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#0B111E] mb-3 flex items-center gap-2">
                  <Briefcase size={16} />
                  Experience
                </h3>
                <div className="space-y-3">
                  {experience.map((exp, index) => (
                    <div key={index} className="bg-white rounded-lg border border-[#EDEDED] p-4">
                      <h4 className="font-medium text-sm text-[#0B111E]">{exp.title}</h4>
                      <p className="text-sm text-[#515151]">{exp.company}</p>
                      <p className="text-xs text-[#757575] mt-1">
                        {exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}
                        {exp.location && ` • ${exp.location}`}
                      </p>
                      {exp.description && (
                        <p className="text-xs text-[#757575] mt-2 line-clamp-3">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education Section */}
            {education.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#0B111E] mb-3 flex items-center gap-2">
                  <GraduationCap size={16} />
                  Education
                </h3>
                <div className="space-y-3">
                  {education.map((edu, index) => (
                    <div key={index} className="bg-white rounded-lg border border-[#EDEDED] p-4">
                      <h4 className="font-medium text-sm text-[#0B111E]">{edu.degree}</h4>
                      <p className="text-sm text-[#515151]">{edu.institution}</p>
                      {edu.fieldOfStudy && (
                        <p className="text-xs text-[#757575]">{edu.fieldOfStudy}</p>
                      )}
                      <p className="text-xs text-[#757575] mt-1">
                        {edu.startYear || edu.startDate} - {edu.endYear || edu.endDate}
                        {edu.gpa && ` • GPA: ${edu.gpa}`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cover Letter */}
            {application.coverLetter && (
              <div>
                <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Cover Letter</h3>
                <div className="bg-white rounded-lg border border-[#EDEDED] p-4">
                  <p className="text-sm text-[#515151] whitespace-pre-wrap">
                    {application.coverLetter}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Candidate Details & Actions */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-[#EDEDED]">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#015FA6] to-[#0288D1] flex items-center justify-center text-white font-bold text-2xl">
                {(application.candidateName || 'C').charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#0B111E]">
                  {application.candidateName || 'Candidate'}
                </h2>
                <p className="text-sm text-[#757575] flex items-center gap-1 mt-1">
                  <Mail size={14} />
                  {application.candidateEmail || 'No email provided'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F1F3F4] rounded-lg transition-colors"
            >
              <X size={20} className="text-[#757575]" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Application Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#F9F9F9] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#757575] mb-1">
                  <Briefcase size={16} />
                  <span className="text-xs font-medium uppercase">Job Applied</span>
                </div>
                <p className="text-sm font-semibold text-[#0B111E]">
                  {application.jobTitle || 'Position'}
                </p>
              </div>

              <div className="bg-[#F9F9F9] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#757575] mb-1">
                  <Calendar size={16} />
                  <span className="text-xs font-medium uppercase">Applied Date</span>
                </div>
                <p className="text-sm font-semibold text-[#0B111E]">
                  {formatDate(application.appliedAt)}
                </p>
              </div>
            </div>

            {/* Current Stage */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Current Application Stage</h3>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border capitalize ${getStageColor(application.currentStage)}`}>
                  {application.currentStage}
                </span>
                {application.updatedAt && (
                  <span className="text-xs text-[#757575]">
                    Last updated: {formatDate(application.updatedAt)}
                  </span>
                )}
              </div>
            </div>

            {/* Stage Change Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Update Application Stage</h3>

              {/* Error Message */}
              {stageError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{stageError}</p>
                    <button
                      onClick={onClearError}
                      className="text-xs text-red-600 hover:underline mt-1"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {/* Available Transitions Info */}
              {getAvailableStages().length > 0 ? (
                <p className="text-xs text-[#757575] mb-3">
                  Available transitions: {getAvailableStages().map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                </p>
              ) : (
                <p className="text-xs text-[#757575] mb-3">
                  No stage transitions available from "{application.currentStage}"
                </p>
              )}

              {/* Stage Buttons */}
              <div className="flex flex-wrap gap-2">
                {STAGE_OPTIONS.map((stage) => {
                  const isCurrentStage = stage.value === application.currentStage;
                  const canTransition = canTransitionTo(stage.value);
                  const isDisabled = isCurrentStage || !canTransition || updatingStage;

                  return (
                    <button
                      key={stage.value}
                      onClick={() => !isDisabled && onStageChange(stage.value)}
                      disabled={isDisabled}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-medium transition-all
                        ${isCurrentStage
                          ? 'bg-[#015FA6] text-white cursor-default'
                          : canTransition
                            ? 'bg-[#F1F3F4] text-[#0B111E] hover:bg-[#E3F2FD] hover:text-[#015FA6]'
                            : 'bg-[#F9F9F9] text-[#A5A5A5] cursor-not-allowed'
                        }
                        ${updatingStage ? 'opacity-50' : ''}
                      `}
                    >
                      {updatingStage && !isDisabled ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          Updating...
                        </span>
                      ) : (
                        stage.label
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Messaging Section */}
            <div className="pt-6 border-t border-[#EDEDED]">
              <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Communication</h3>

              {canMessage ? (
                <button
                  onClick={onMessage}
                  className="w-full py-3 px-4 bg-[#015FA6] text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#014580] transition-colors"
                >
                  <MessageSquare size={18} />
                  Message Candidate
                </button>
              ) : (
                <div className="bg-[#E3F2FD] border border-[#90CAF9] rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <MessageSquare size={20} className="text-[#1976D2] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#1565C0]">Start a Conversation</p>
                      <p className="text-xs text-[#1976D2] mt-1">
                        Find this candidate a fit? Upgrade the status to "<strong>{messagingAllowedStages.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('" or "')}</strong>" to message them.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#EDEDED] bg-[#F9F9F9]">
            <button
              onClick={onClose}
              className="w-full py-3 px-4 border border-[#EDEDED] text-[#515151] rounded-lg font-medium hover:bg-white transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfileModal;
